import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "mm_pv_session";
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 min sliding window

function getSessionId(): string {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { id: string; ts: number };
      if (Date.now() - parsed.ts < SESSION_TTL_MS) {
        localStorage.setItem(SESSION_KEY, JSON.stringify({ id: parsed.id, ts: Date.now() }));
        return parsed.id;
      }
    }
  } catch {}
  const id = crypto.randomUUID();
  localStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: Date.now() }));
  return id;
}

/**
 * Logs every route change to public.page_views.
 * Skips admin/portal/auth routes to avoid polluting analytics.
 * Sends a heartbeat every 60s so live-visitor counts stay accurate.
 */
export function PageViewTracker() {
  const location = useLocation();
  const lastPath = useRef<string>("");

  const skip = (path: string) =>
    path.startsWith("/admin") ||
    path.startsWith("/client-portal") ||
    path.startsWith("/login") ||
    path.startsWith("/reset-password") ||
    path.startsWith("/change-password");

  useEffect(() => {
    const path = location.pathname + location.search;
    if (skip(location.pathname)) return;
    if (path === lastPath.current) return;
    lastPath.current = path;

    const sessionId = getSessionId();
    const referrer = document.referrer && !document.referrer.includes(window.location.host)
      ? document.referrer
      : null;

    supabase
      .from("page_views")
      .insert({
        path: location.pathname,
        referrer,
        session_id: sessionId,
        user_agent: navigator.userAgent.slice(0, 500),
      } as any)
      .then(() => {});
  }, [location.pathname, location.search]);

  // Heartbeat: refresh session timestamp + insert a no-op ping every 60s while visible
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      if (skip(window.location.pathname)) return;
      const sessionId = getSessionId();
      supabase
        .from("page_views")
        .insert({
          path: window.location.pathname,
          referrer: null,
          session_id: sessionId,
          user_agent: "heartbeat",
        } as any)
        .then(() => {});
    };
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, []);

  return null;
}